// Service
import { DropdownHttpService } from '@core/services/dropdown-http.service';
import { AppPermissionService } from '@core/services/app-permission.service';
import { MemberDataService } from "../../general/members/services/member-data.service";
import { PromotionSettingHttpService } from '@core/services/promotion-setting-http.service';
import { TransactionHttpService } from '@core/services/transaction-http.service';
import { SmarticoPromotionLogDataService } from './services/smartico-promotion-log-data.service';
import { SmarticoPromotionLogEntityService } from './services/smartico-promotion-log-entity.service';
// Angular / Core
import { FormGroup, FormControl } from "@angular/forms";
import { Component, OnInit, ChangeDetectorRef } from "@angular/core";
import { LoadingBarService } from "@ngx-loading-bar/core";
import { Router } from '@angular/router';
// Third Party
import * as moment from 'moment-timezone';
import { Pagination } from '@core/models/pagination.model';
import { Observable, of, Subscription, Subject } from 'rxjs';
import { tap, map, distinctUntilChanged, debounceTime } from 'rxjs/operators';
import { MatDialog } from "@angular/material/dialog";
// models
import { Promotion } from '@core/models/promotion.model';
import { SmarticoPromotionLog } from '@core/models/smartico-promotion-log.model';
// dialog
import { MemberInformationDialogComponent } from "../../general/members/dialogs/member-information/member-information.component";


@Component({
  selector: "kt-smartico-promotion-logs",
  templateUrl: "./smartico-promotion-logs.component.html",
  styleUrls: ["./smartico-promotion-logs.component.scss"],
})

export class SmarticoPromotionLogsComponent implements OnInit {

  form: FormGroup;
  loading = false;
  clearBtnLoading = false;
  searchBtnLoading = false;
  promoTypeahead = [];
  dateTimeFilterType = [
    {
      key: 'created_at',
      label: 'Created Date'
    },
  ]
  pagination: Pagination;
  pageSize = 30;
  page = 1;
  maxSize = 5;
  params = '';
  ranges: any;
  dataLength: number;
  timezone = JSON.parse(localStorage.getItem('user_data')).timezone;

  logs$: Observable<SmarticoPromotionLog[]>;
  messages$ = this.smarticoPromotionLogDataService.messages$;
  messages2$ = new Subject<any[]>();

  sortingConfig = {
    'id': 'desc',
    'username': 'desc',
    'promo_code': 'desc',
    'smartico_bonus_id': 'desc',
    'created_at': 'desc',
  };
  sortingSelection = {
    'sort_by': 'id',
    'sort_order': 'desc',
  };

  dropdown = {
    currencies: JSON.parse(sessionStorage.getItem('currencies')) === null ? [] : JSON.parse(sessionStorage.getItem('currencies')),
    perPage: this.dropdownHttpService.perPage
  };

  statuses = {
    0: 'Pending',
    1: 'Claimed',
    2: 'Expired',
    3: 'Cancelled',
    4: 'Pending Approval',
    5: 'Inactive',
    6: 'Failed',
  };

  // permissions
  canViewSmarticoPromotionLogs: boolean;
  canViewMemberDialog: boolean;
  canAccessLinkedMemberPromo: boolean;

  private subscriptions = new Subscription();
  

  constructor(
    private loadingBar: LoadingBarService,
    private dropdownHttpService: DropdownHttpService,
    public dialog: MatDialog,
    private promotionSettingHttpService: PromotionSettingHttpService,
    private transactionHttpService: TransactionHttpService,
    private smarticoPromotionLogEntityService: SmarticoPromotionLogEntityService,
    private smarticoPromotionLogDataService: SmarticoPromotionLogDataService,
    private memberDataService: MemberDataService,
    private appPermissionService: AppPermissionService,
    private cdr: ChangeDetectorRef,
    private router: Router,
  ) { }

  ngOnInit() {
    // Subscribe to promotion event
    this.promotionSettingHttpService.getAllPromoSettings().subscribe((res: any) => {
      this.promoTypeahead = res.promotions;
    });

    // Init form fields
    this.formInit();

    // Permissions
    const apSub = this.appPermissionService.getAppPermissions().subscribe((appPermissions) => {
      this.canViewSmarticoPromotionLogs = appPermissions.view_crm_smartico_promotion_log;
      this.canViewMemberDialog = appPermissions.crm_smartico_view_member_dialog;
      this.canAccessLinkedMemberPromo = appPermissions.crm_smartico_link_member_promo;
      this.cdr.detectChanges();
    });
    this.subscriptions.add(apSub);
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  searchPromo = (search$: Observable<string>): Observable<Promotion[]> =>{
    return search$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      map(term => term.trim() === '' ? [] : this.filterPromo(term))
    )
  }

  filterPromo(term: string): Promotion[] {
    if (this.promoTypeahead) {
      return this.promoTypeahead.filter(
        promo => promo.code.toLowerCase().includes(term.toLowerCase()) || promo.name.toLowerCase().includes(term.toLowerCase())
      );
    } else {
      return [];
    }
  }

  formatPromo(promotion: Promotion): string {
    return `${promotion.code} - ${promotion.name}`;
  }

  private formInit() {
    let defaultStart = this.transactionHttpService.getToday().from;
    let defaultEnd = this.transactionHttpService.getToday().to;

    this.form = new FormGroup({
      username: new FormControl(null),
      promotion: new FormControl({value: null, disabled: false}),
      smartico_id: new FormControl(null),
      date_type: new FormControl(this.dateTimeFilterType[0].key),
      start_date_time: new FormControl(defaultStart),
      end_date_time: new FormControl(defaultEnd),
      defaultDate: new FormControl({
        startDate: defaultStart,
        endDate: defaultEnd,
      }), // Do not remove: For Clearing The Range
    });
  }

  onSubmit(clearSearch?: boolean) {
    this.timezone = JSON.parse(localStorage.getItem('user_data')).timezone;
    this.searchBtnLoading = clearSearch ? false : true;
    const formValue = this.form.value;
    if (formValue.promotion) {
      if (formValue.promotion.code != undefined && formValue.promotion.name != undefined) {
        formValue.code = formValue.promotion.code;
        formValue.promotion_name = formValue.promotion.name;
        delete formValue.promotion;
      }
    }
    of(this.form.value).pipe(
      map(this.filterFormFields),
      tap((data) => {
        if (data['start_date_time']) {
          data['start_date_time'] = moment(data['start_date_time']).tz(this.timezone, true).utc().format('YYYY-MM-DD HH:mm:ss');
          data['end_date_time'] = moment(data['end_date_time']).tz(this.timezone, true).utc().format('YYYY-MM-DD HH:mm:ss');
        }
        
        this.params = Object.keys(data).map(key => key + '=' + data[key]).join('&');
        this.page = 1;
        this.onViewPageBy();
      })
    ).subscribe();
  }

  private filterFormFields(formData: any) {
    const fields = {};
    Object.keys(formData).forEach(key => {
      if (formData[key] !== '' && formData[key] !== null && formData[key] !== undefined && key !== 'defaultDate' && formData[key] !== 'all') {
        fields[key] = formData[key];
      }
    });
    return fields;
  }

  onClear() {
    this.formInit();
    this.onSubmit();
  }

  onClearDate() {
    if (this.form.value.start_date_time !== null) {
      let defaultStart = this.transactionHttpService.getToday().from;
      let defaultEnd = this.transactionHttpService.getToday().to;

      this.form.patchValue({
        start_date_time: defaultStart,
        end_date_time: defaultEnd,
        defaultDate: {
          startDate: defaultStart,
          endDate: defaultEnd,
        }, // Do not remove: For Clearing The Range
      });
    }
  }

  onDateRange(event: any) {
    if (event) {
      this.form.patchValue({
        start_date_time: event.startDate !== null && event.startDate !== undefined ? event.startDate : null,
        end_date_time: event.endDate !== null && event.endDate !== undefined ? event.endDate : null
      });
    }
  }

  updateDateRange() {
    this.ranges = this.transactionHttpService.updateDateRange();
  }

  onViewPageBy(page = 1, pageSize?: number, params?: string) {
    this.loading = true;
    pageSize = this.pageSize;
    params = this.params ? `&${this.params}` : '';
    this.loadingBar.start();
    return this.logs$ = this.smarticoPromotionLogDataService.getWithQuery(`?page=${page}&perPage=${pageSize}&${this.generateSortingParam()}${params}`).pipe(
      tap(res => {
        this.loading = false;
        this.clearBtnLoading = false;
        this.searchBtnLoading = false;
        this.dataLength = res.length;
        this.pagination = this.smarticoPromotionLogDataService.pagination;
        this.loadingBar.complete();
      })
    );
  }

  private generateSortingParam() {
    const sortingParams = Object.keys(this.sortingSelection).map(key => key + '=' + this.sortingSelection[key]).join('&');
    return sortingParams;
  }

  onSortColumn(property: string) {
    // Reset other columns
    for (const key in this.sortingConfig) {
      if (Object.prototype.hasOwnProperty.call(this.sortingConfig, key)) {
        if (key == property) {
          this.sortingConfig[key] = this.sortingConfig[key] === 'asc' ? 'desc' : 'asc';
        } else {
          this.sortingConfig[key] = 'desc';
        }

      }
    }
    // User selection
    this.sortingSelection.sort_by = property;

    if (this.sortingSelection.sort_by === property) {
      // Same column
      this.sortingSelection.sort_order = this.sortingConfig[property];
    } else {
      // Switch to other column
      this.sortingConfig[property] = 'asc';
      this.sortingSelection.sort_order = 'asc';
    }
    // Load Data
    this.onViewPageBy(this.page, this.pageSize, this.params);
  }

  onOpenDialog(type: string, member_account_id?: any) {
    if (type === 'member-information') {
      if (!this.canViewMemberDialog) {
        this.messages2$.next(['You do not have permission to view member dialog.']);
        return;
      }

      const member = this.memberDataService.getById(member_account_id, `?start_date=${moment(this.transactionHttpService.getYesterday().from).tz(this.timezone, true).utc().format('YYYY-MM-DD HH:mm:ss')}&end_date=${moment(this.transactionHttpService.getLast24Hours().to).tz(this.timezone, true).utc().format('YYYY-MM-DD HH:mm:ss')}&additional_info=1`);
      const memberSubs  = member.pipe(
        tap((res) => {
          if (res) {
            this.openDialogBy(MemberInformationDialogComponent, { mode: 'member-information', member: res });
          }
        })
      ).subscribe();

      this.subscriptions.add(memberSubs);
    }
  }

  private openDialogBy(componentRef: any, data?: { mode?: any, member?: any }) {
    const dialogRef = this.dialog.open(componentRef, {
      width: data.mode === 'member-information' ? '1500px' : '800px',
      height: data.mode === 'member-information' ? '80vh' : 'auto',
      data: {
        mode: data.mode,
        member: data.member,
      },
    });

    if (data.mode !== 'member-information') {
      dialogRef.afterClosed().subscribe((result) => {
        if (result === true) {
          this.onViewPageBy(this.page).subscribe();
        }
      });
    }
  }

  linkPromotion(promotion_id: string) {
    const autoSearchKey = 'promotion_auto_search_by_id';
    localStorage.setItem(autoSearchKey, promotion_id);
    // redirect to 3.2
    window.open('/general/promotion-codes', '_blank');
  }
}